import { useEffect } from "react"
import { echo } from "@/lib/echo"
import { queryClient } from "@/lib/query-client"
import { useNotificationStore } from "@/stores/notification-store"

export function useRoomChannel(roomId: string | undefined) {
  useEffect(() => {
    if (!roomId) return

    const channelName = `room.${roomId}`
    const channel = echo.private(channelName)

    const onReservationCreated = () => {
      queryClient.invalidateQueries({ queryKey: ["rooms", roomId] })
      queryClient.invalidateQueries({ queryKey: ["reservations"] })
    }
    const onReservationUpdated = () => {
      queryClient.invalidateQueries({ queryKey: ["rooms", roomId] })
      queryClient.invalidateQueries({ queryKey: ["reservations"] })
    }
    const onReservationCancelled = () => {
      queryClient.invalidateQueries({ queryKey: ["rooms", roomId] })
      queryClient.invalidateQueries({ queryKey: ["reservations"] })
    }
    const onReservationEnded = () => {
      queryClient.invalidateQueries({ queryKey: ["rooms", roomId] })
      queryClient.invalidateQueries({ queryKey: ["reservations"] })
    }
    const onCheckInPerformed = () => {
      queryClient.invalidateQueries({ queryKey: ["rooms", roomId] })
      queryClient.invalidateQueries({ queryKey: ["check-ins"] })
    }
    const onRoomReleased = () => {
      queryClient.invalidateQueries({ queryKey: ["rooms", roomId] })
    }
    const onMeetingRoomStatusChanged = (payload: {
      reservation_id: string | null
    }) => {
      if (!payload.reservation_id) return
      queryClient.invalidateQueries({
        queryKey: ["meeting-room", payload.reservation_id],
      })
      queryClient.invalidateQueries({
        queryKey: ["reservations", payload.reservation_id],
      })
    }

    channel
      .listen("ReservationCreated", onReservationCreated)
      .listen("ReservationUpdated", onReservationUpdated)
      .listen("ReservationCancelled", onReservationCancelled)
      .listen("ReservationEnded", onReservationEnded)
      .listen("CheckInPerformed", onCheckInPerformed)
      .listen("RoomReleased", onRoomReleased)
      .listen(".meeting-room.status-changed", onMeetingRoomStatusChanged)

    return () => {
      channel.stopListening("ReservationCreated", onReservationCreated)
      channel.stopListening("ReservationUpdated", onReservationUpdated)
      channel.stopListening("ReservationCancelled", onReservationCancelled)
      channel.stopListening("ReservationEnded", onReservationEnded)
      channel.stopListening("CheckInPerformed", onCheckInPerformed)
      channel.stopListening("RoomReleased", onRoomReleased)
      channel.stopListening(
        ".meeting-room.status-changed",
        onMeetingRoomStatusChanged,
      )
      echo.leave(channelName)
    }
  }, [roomId])
}

export function useUserChannel(userId: string | undefined) {
  const increment = useNotificationStore((state) => state.increment)

  useEffect(() => {
    if (!userId) return

    const channelName = `user.${userId}`
    const channel = echo.private(channelName)

    const onNotificationReceived = () => {
      increment()
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    }
    const onParticipantInvited = () => {
      queryClient.invalidateQueries({ queryKey: ["reservations"] })
    }
    const onParticipantResponded = () => {
      queryClient.invalidateQueries({ queryKey: ["reservations"] })
    }

    channel
      .listen("NotificationReceived", onNotificationReceived)
      .listen("ParticipantInvited", onParticipantInvited)
      .listen("ParticipantResponded", onParticipantResponded)

    return () => {
      channel.stopListening("NotificationReceived", onNotificationReceived)
      channel.stopListening("ParticipantInvited", onParticipantInvited)
      channel.stopListening("ParticipantResponded", onParticipantResponded)
      echo.leave(channelName)
    }
  }, [userId, increment])
}

export function useOrganizationChannel(orgId: string | undefined) {
  useEffect(() => {
    if (!orgId) return

    const channelName = `organization.${orgId}`
    const channel = echo.private(channelName)

    const onRoomStatusChanged = () => {
      queryClient.invalidateQueries({ queryKey: ["rooms"] })
    }

    channel.listen("RoomStatusChanged", onRoomStatusChanged)

    return () => {
      channel.stopListening("RoomStatusChanged", onRoomStatusChanged)
      echo.leave(channelName)
    }
  }, [orgId])
}

export function useDeviceChannel(deviceId: string | undefined) {
  useEffect(() => {
    if (!deviceId) return

    const channelName = `device.${deviceId}`
    const channel = echo.private(channelName)

    const onTabletRefresh = () => {
      queryClient.invalidateQueries({ queryKey: ["rooms"] })
      queryClient.invalidateQueries({ queryKey: ["reservations"] })
    }

    channel.listen("TabletRefresh", onTabletRefresh)

    return () => {
      channel.stopListening("TabletRefresh", onTabletRefresh)
      echo.leave(channelName)
    }
  }, [deviceId])
}
